import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCookies } from "react-cookie";

import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import Container from "react-bootstrap/Container";
import Form from "react-bootstrap/Form";

import NavBar from "../../../components/NavBar";
import styles from "../../../styles/Profile.module.css";
import redcap_styles from "../../../apps/redcap-perms/styles/Main.module.css";

import { tokenIsValid } from "../../../utils/token";
import { DailyCallsExamTypes } from "../../../constants/apps";
import { NavigateRoutes } from "../../../constants/routes";
import { submitDailyCallsExam } from "../../../api/DailyCallsExams";

const NewDailyCallsExam = () => {
  const navigate = useNavigate();
  const [cookies] = useCookies(["token"]);
  const [authorized, setAuthorized] = useState(false);
  const [pageDoneLoading, setPageDoneLoading] = useState(false);

  useEffect(() => {
    if (!cookies["token"] || !tokenIsValid(cookies["token"])) {
      navigate(NavigateRoutes.LOGIN);
    } else {
      setAuthorized(true);
    }
    setPageDoneLoading(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [raFirstName, setRaFirstName] = useState("");
  const [raLastName, setRaLastName] = useState("");
  const [raEmail, setRaEmail] = useState("");
  const [raPhone, setRaPhone] = useState("");
  const [examType, setExamType] = useState("");
  const [examDate, setExamDate] = useState("");
  const [examStartTime, setExamStartTime] = useState("");
  const [examEndTime, setExamEndTime] = useState("");
  const [schedulerEmail, setSchedulerEmail] = useState("");
  const [administratorFullName, setAdministratorFullName] = useState("");
  const [administratorEmail, setAdministratorEmail] = useState("");
  const [administratorPhone, setAdministratorPhone] = useState("");
  const [administratorZoomUrl, setAdministratorZoomUrl] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const resetForm = () => {
    setRaFirstName("");
    setRaLastName("");
    setRaEmail("");
    setRaPhone("");
    setExamType("");
    setExamDate("");
    setExamStartTime("");
    setExamEndTime("");
    setSchedulerEmail("");
    setAdministratorFullName("");
    setAdministratorEmail("");
    setAdministratorPhone("");
    setAdministratorZoomUrl("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitSuccess(false);

    // both are HH:MM, so comparing the strings compares the times
    if (examEndTime <= examStartTime) {
      setSubmitError("The end time must be after the start time.");
      return;
    }

    setSubmitting(true);
    setSubmitError("");

    submitDailyCallsExam(
      {
        ra_first_name: raFirstName,
        ra_last_name: raLastName,
        ra_email: raEmail,
        ra_phone: raPhone,
        exam_type: examType,
        exam_date: examDate,
        exam_start_time: examStartTime,
        exam_end_time: examEndTime,
        scheduler_email: schedulerEmail,
        administrator_full_name: administratorFullName,
        administrator_email: administratorEmail,
        administrator_phone: administratorPhone,
        administrator_zoom_url: administratorZoomUrl,
      },
      cookies["token"],
    )
      .then(() => {
        setSubmitSuccess(true);
        resetForm();
      })
      .catch((err) => {
        const detail =
          err.response?.data?.detail || "An error occurred. Please try again.";
        // FastAPI reports a malformed request as a list rather than a message
        setSubmitError(
          typeof detail === "string" ? detail : "An error occurred. Please try again.",
        );
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <div>
      <NavBar />
      {pageDoneLoading && authorized && (
        <Container className={styles.pageContainer}>
          <Container>
            <h1>Schedule Daily Calls Exam</h1>
          </Container>

          <Form onSubmit={handleSubmit}>

            <Container className={redcap_styles.container}>
              <Container className={redcap_styles.functionContainer}>
                <h4>RA Info</h4>

                <Container className={styles.attributeContainer}>
                  <Container className={styles.attributeName}>First name</Container>
                  <Container>
                    <Form.Control
                      type="text"
                      value={raFirstName}
                      onChange={(e) => setRaFirstName(e.target.value)}
                      required
                    />
                  </Container>
                </Container>

                <Container className={styles.attributeContainer}>
                  <Container className={styles.attributeName}>Last name</Container>
                  <Container>
                    <Form.Control
                      type="text"
                      value={raLastName}
                      onChange={(e) => setRaLastName(e.target.value)}
                      required
                    />
                  </Container>
                </Container>

                <Container className={styles.attributeContainer}>
                  <Container className={styles.attributeName}>Email</Container>
                  <Container>
                    <Form.Control
                      type="email"
                      value={raEmail}
                      onChange={(e) => setRaEmail(e.target.value)}
                      required
                    />
                  </Container>
                </Container>

                <Container className={styles.attributeContainer}>
                  <Container className={styles.attributeName}>Phone</Container>
                  <Container>
                    <Form.Control
                      type="text"
                      value={raPhone}
                      onChange={(e) => setRaPhone(e.target.value)}
                    />
                  </Container>
                </Container>
              </Container>
            </Container>

            <Container className={redcap_styles.container}>
              <Container className={redcap_styles.functionContainer}>
                <h4>Exam Info</h4>

                <Container className={styles.attributeContainer}>
                  <Container className={styles.attributeName}>Exam type</Container>
                  <Container>
                    <Form.Select
                      value={examType}
                      onChange={(e) => setExamType(e.target.value)}
                      required
                    >
                      <option value="">Select an exam type</option>
                      {DailyCallsExamTypes.map((type) => (
                        <option key={type}>{type}</option>
                      ))}
                    </Form.Select>
                  </Container>
                </Container>

                <Container className={styles.attributeContainer}>
                  <Container className={styles.attributeName}>Exam date</Container>
                  <Container>
                    <Form.Control
                      type="date"
                      value={examDate}
                      onChange={(e) => setExamDate(e.target.value)}
                      required
                    />
                  </Container>
                </Container>

                <Container className={styles.attributeContainer}>
                  <Container className={styles.attributeName}>Start time</Container>
                  <Container>
                    <Form.Control
                      type="time"
                      value={examStartTime}
                      onChange={(e) => setExamStartTime(e.target.value)}
                      required
                    />
                  </Container>
                </Container>

                <Container className={styles.attributeContainer}>
                  <Container className={styles.attributeName}>End time</Container>
                  <Container>
                    <Form.Control
                      type="time"
                      value={examEndTime}
                      onChange={(e) => setExamEndTime(e.target.value)}
                      required
                    />
                  </Container>
                </Container>

                <Container className={styles.attributeContainer}>
                  <Container className={styles.attributeName}>Scheduler email</Container>
                  <Container>
                    <Form.Control
                      type="email"
                      value={schedulerEmail}
                      onChange={(e) => setSchedulerEmail(e.target.value)}
                      required
                    />
                    <Form.Text muted>
                      Copied on the emails to the exam administrator: the
                      confirmation now, and any rescheduling, cancellation or
                      no show later.
                    </Form.Text>
                  </Container>
                </Container>
              </Container>
            </Container>

            <Container className={redcap_styles.container}>
              <Container className={redcap_styles.functionContainer}>
                <h4>Exam Administrator Info</h4>

                <Container className={styles.attributeContainer}>
                  <Container className={styles.attributeName}>Full name</Container>
                  <Container>
                    <Form.Control
                      type="text"
                      value={administratorFullName}
                      onChange={(e) => setAdministratorFullName(e.target.value)}
                      required
                    />
                  </Container>
                </Container>

                <Container className={styles.attributeContainer}>
                  <Container className={styles.attributeName}>Email</Container>
                  <Container>
                    <Form.Control
                      type="email"
                      value={administratorEmail}
                      onChange={(e) => setAdministratorEmail(e.target.value)}
                      required
                    />
                  </Container>
                </Container>

                <Container className={styles.attributeContainer}>
                  <Container className={styles.attributeName}>Phone</Container>
                  <Container>
                    <Form.Control
                      type="text"
                      value={administratorPhone}
                      onChange={(e) => setAdministratorPhone(e.target.value)}
                    />
                  </Container>
                </Container>

                <Container className={styles.attributeContainer}>
                  <Container className={styles.attributeName}>Zoom meeting URL</Container>
                  <Container>
                    <Form.Control
                      type="text"
                      value={administratorZoomUrl}
                      onChange={(e) => setAdministratorZoomUrl(e.target.value)}
                      required
                    />
                  </Container>
                </Container>
              </Container>
            </Container>

            {submitSuccess && (
              <Alert variant="success" className="mx-3">
                Exam scheduled. Confirmation emails have been sent.
              </Alert>
            )}

            {submitError && (
              <Alert variant="danger" className="mx-3">
                {submitError}
              </Alert>
            )}

            <Container className={styles.sectionContainer}>
              <Container fluid className={styles.buttonContainer}>
                <Button type="submit" variant="warning" disabled={submitting}>
                  {submitting ? "Submitting..." : "Schedule exam"}
                </Button>
              </Container>
            </Container>

          </Form>
        </Container>
      )}
    </div>
  );
};

export default NewDailyCallsExam;
